import {useEffect, useState} from 'react'
import {
  Plus,
  Timer,
  Trash2,
  Link2,
  X,
  Search,
  Pencil,
  Play,
  CheckCircle2,
  RotateCcw,
  CalendarDays,
} from 'lucide-react'
import {useApp} from '../../context/AppContext'
import {
  getStatusConfig,
  getPriorityConfig,
  getSprintStatusConfig,
  formatDate,
  isTaskOverdue,
} from '../../utils/helpers'
import type {Sprint, SprintStatus} from '../../types'
import Badge from '../common/Badge'
import Avatar from '../common/Avatar'
import SprintCard from './SprintCard'
import SprintForm from './SprintForm'
import ConfirmDialog from '../common/ConfirmDialog'
import * as api from '../../services/api'
import PageHeader from '../common/PageHeader'

export default function SprintList() {
  const {state, dispatch, getUserById, getTasksForSprint, apiDeleteSprint, apiUpdateSprint} =
    useApp()
  const [selectedSprintId, setSelectedSprintId] = useState<string | null>(null)
  const [formSprint, setFormSprint] = useState<Sprint | 'new' | null>(null)
  const [deleteSprintId, setDeleteSprintId] = useState<string | null>(null)
  const [transition, setTransition] = useState<{id: string; status: SprintStatus} | null>(null)
  const [showTaskPicker, setShowTaskPicker] = useState(false)
  const [assigning, setAssigning] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [boardId, setBoardId] = useState('all')
  const [status, setStatus] = useState('all')
  const [taskSearch, setTaskSearch] = useState('')
  const [taskFilter, setTaskFilter] = useState('all')
  const [pickerSearch, setPickerSearch] = useState('')
  const query = search.trim().toLocaleLowerCase('pt-BR')
  const sprints = state.sprints
    .filter(
      (sprint) =>
        (boardId === 'all' || sprint.boardId === boardId) &&
        (status === 'all' || sprint.status === status) &&
        (sprint.name + ' ' + sprint.goal).toLocaleLowerCase('pt-BR').includes(query)
    )
    .sort(
      (a, b) =>
        ({active: 0, planning: 1, completed: 2}[a.status] -
          {active: 0, planning: 1, completed: 2}[b.status] ||
        b.startDate.localeCompare(a.startDate))
    )
  const firstSprintId = sprints[0]?.id || null
  const hasSelection = sprints.some((sprint) => sprint.id === selectedSprintId)
  useEffect(() => {
    if (!hasSelection) setSelectedSprintId(firstSprintId)
  }, [hasSelection, firstSprintId])
  useEffect(() => {
    setShowTaskPicker(false)
    setTaskSearch('')
    setTaskFilter('all')
    setPickerSearch('')
    setError('')
  }, [selectedSprintId])
  const selectedSprint = state.sprints.find((sprint) => sprint.id === selectedSprintId)
  const sprintTasks = selectedSprintId ? getTasksForSprint(selectedSprintId) : []
  const completed = sprintTasks.filter((task) => task.status === 'done')
  const pendingCount = sprintTasks.length - completed.length
  const progress = sprintTasks.length
    ? Math.round((completed.length / sprintTasks.length) * 100)
    : 0
  const selectedBoard = state.boards.find((board) => board.id === selectedSprint?.boardId)
  const availableTasks = state.tasks.filter(
    (task) =>
      task.boardId === selectedSprint?.boardId &&
      !task.sprintId &&
      task.title.toLocaleLowerCase('pt-BR').includes(pickerSearch.trim().toLocaleLowerCase('pt-BR'))
  )
  const visibleTasks = sprintTasks.filter(
    (task) =>
      task.title
        .toLocaleLowerCase('pt-BR')
        .includes(taskSearch.trim().toLocaleLowerCase('pt-BR')) &&
      (taskFilter === 'all' ||
        (taskFilter === 'overdue'
          ? isTaskOverdue(task)
          : taskFilter === 'done'
          ? task.status === 'done'
          : task.status !== 'done'))
  )
  const clearFilters = () => {
    setSearch('')
    setBoardId('all')
    setStatus('all')
  }

  const handleAssignment = async (taskId: string, sprintId: string | null) => {
    if (assigning) return
    const task = state.tasks.find((item) => item.id === taskId)
    if (!task) return
    setAssigning(taskId)
    setError('')
    try {
      await api.assignTaskToSprint(taskId, sprintId)
      dispatch({type: 'UPDATE_TASK', payload: {...task, sprintId}})
    } catch {
      setError('Não foi possível atualizar o vínculo da tarefa. Tente novamente.')
    } finally {
      setAssigning(null)
    }
  }
  const statusAction =
    selectedSprint?.status === 'planning'
      ? {status: 'active' as SprintStatus, label: 'Iniciar sprint', icon: Play}
      : selectedSprint?.status === 'active'
      ? {status: 'completed' as SprintStatus, label: 'Concluir sprint', icon: CheckCircle2}
      : {status: 'active' as SprintStatus, label: 'Reabrir sprint', icon: RotateCcw}
  const StatusIcon = statusAction.icon
  const transitioningSprint = state.sprints.find((sprint) => sprint.id === transition?.id)
  const transitionPending = transition
    ? getTasksForSprint(transition.id).filter((task) => task.status !== 'done').length
    : 0

  return (
    <div className='devops-page'>
      <PageHeader
        eyebrow='Ciclos de entrega'
        icon={Timer}
        title='Sprints'
        description='Planeje os ciclos, acompanhe o objetivo da equipe e gerencie as tarefas de cada entrega.'
        meta={
          <>
            <span>{state.sprints.length} ciclos</span>
            <span>
              {state.sprints.filter((sprint) => sprint.status === 'active').length} ativos
            </span>
            <span>
              {state.sprints.filter((sprint) => sprint.status === 'planning').length} em
              planejamento
            </span>
          </>
        }
        actions={
          <button
            onClick={() => setFormSprint('new')}
            disabled={!state.boards.length}
            className='devops-primary-button disabled:opacity-50'
          >
            <Plus size={16} />
            Novo sprint
          </button>
        }
      />
      <div className='devops-surface p-4 flex flex-wrap items-center gap-3'>
        <div className='flex items-center gap-2 flex-1 min-w-[200px]'>
          <Search size={17} className='text-slate-400 shrink-0' />
          <input
            aria-label='Buscar sprints'
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder='Buscar sprint ou objetivo...'
            className='w-full px-3 py-2 rounded-xl border border-slate-200 text-sm'
          />
        </div>
        <select
          aria-label='Filtrar sprints por quadro'
          value={boardId}
          onChange={(event) => setBoardId(event.target.value)}
          className='px-3 py-2 rounded-xl border border-slate-200 text-sm'
        >
          <option value='all'>Todos os quadros</option>
          {state.boards.map((board) => (
            <option key={board.id} value={board.id}>
              {board.name}
            </option>
          ))}
        </select>
        <select
          aria-label='Filtrar status dos sprints'
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          className='px-3 py-2 rounded-xl border border-slate-200 text-sm'
        >
          <option value='all'>Todos os status</option>
          <option value='planning'>Planejamento</option>
          <option value='active'>Ativos</option>
          <option value='completed'>Concluídos</option>
        </select>
        {(search || boardId !== 'all' || status !== 'all') && (
          <button className='devops-secondary-button' onClick={clearFilters}>
            Limpar filtros
          </button>
        )}
      </div>
      {!state.boards.length && (
        <p className='p-4 bg-amber-50 rounded-xl text-sm text-amber-700'>
          Crie um quadro para começar a planejar os sprints.
        </p>
      )}
      <div className='flex flex-col lg:flex-row gap-5'>
        <aside className='lg:w-80 xl:w-96 shrink-0 space-y-3' aria-label='Lista de sprints'>
          <div className='flex justify-between items-center px-1'>
            <h2 className='text-sm font-semibold text-slate-700'>Ciclos de trabalho</h2>
            <span className='text-xs text-slate-500' role='status'>
              {sprints.length} de {state.sprints.length}
            </span>
          </div>
          {sprints.length === 0 && (
            <div className='devops-empty-state px-5 py-8'>
              <Timer size={24} />
              <div>
                <h3>
                  {state.sprints.length
                    ? 'Nenhum sprint encontrado'
                    : 'Seu próximo ciclo começa aqui'}
                </h3>
                <p>
                  {state.sprints.length
                    ? 'Ajuste a busca ou remova os filtros.'
                    : 'Crie um sprint, defina o objetivo e adicione as tarefas da entrega.'}
                </p>
              </div>
              {state.sprints.length ? (
                <button className='devops-secondary-button' onClick={clearFilters}>
                  Limpar filtros
                </button>
              ) : (
                state.boards.length > 0 && (
                  <button className='devops-primary-button' onClick={() => setFormSprint('new')}>
                    Criar primeiro sprint
                  </button>
                )
              )}
            </div>
          )}
          {sprints.map((sprint) => (
            <SprintCard
              key={sprint.id}
              sprint={sprint}
              onSelect={setSelectedSprintId}
              isSelected={selectedSprintId === sprint.id}
            />
          ))}
        </aside>
        <div className='flex-1 min-w-0'>
          {selectedSprint && hasSelection ? (
            <div className='devops-table-shell'>
              <div className='p-4 md:p-5 space-y-4 border-b border-slate-100'>
                <div className='flex flex-wrap items-start justify-between gap-3'>
                  <div>
                    <div className='flex flex-wrap items-center gap-2'>
                      <h3 className='text-lg font-semibold text-slate-800'>
                        {selectedSprint.name}
                      </h3>
                      <Badge className={getSprintStatusConfig(selectedSprint.status).color}>
                        {getSprintStatusConfig(selectedSprint.status).label}
                      </Badge>
                    </div>
                    <p className='text-xs text-slate-500 mt-1'>
                      {selectedBoard?.name || 'Quadro indisponível'}
                    </p>
                  </div>
                  <div className='flex items-center gap-2'>
                    <button
                      onClick={() => setFormSprint(selectedSprint)}
                      className='devops-icon-button'
                      aria-label='Editar sprint'
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => setDeleteSprintId(selectedSprint.id)}
                      className='devops-icon-button hover:!text-red-500'
                      aria-label='Excluir sprint'
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <p className='text-sm text-slate-600'>
                  {selectedSprint.goal || 'Defina o objetivo deste ciclo em Editar sprint.'}
                </p>
                <div className='flex flex-wrap gap-3 items-center justify-between'>
                  <p className='flex items-center gap-1.5 text-xs text-slate-500'>
                    <CalendarDays size={14} />
                    {formatDate(selectedSprint.startDate)} — {formatDate(selectedSprint.endDate)}
                  </p>
                  <button
                    className='devops-secondary-button'
                    onClick={() =>
                      setTransition({id: selectedSprint.id, status: statusAction.status})
                    }
                  >
                    <StatusIcon size={15} />
                    {statusAction.label}
                  </button>
                </div>
                <div className='grid grid-cols-3 gap-3'>
                  {[
                    ['Concluídas', completed.length + '/' + sprintTasks.length],
                    ['Pendentes', pendingCount],
                    [
                      'Story points',
                      sprintTasks.reduce((total, task) => total + task.storyPoints, 0),
                    ],
                  ].map(([label, value]) => (
                    <div key={label} className='p-3 bg-slate-50 rounded-xl'>
                      <p className='text-lg font-semibold text-slate-800'>{value}</p>
                      <p className='text-xs text-slate-500'>{label}</p>
                    </div>
                  ))}
                </div>
                <div>
                  <div className='flex justify-between text-xs text-slate-500 mb-1.5'>
                    <span>Progresso das tarefas</span>
                    <span>{progress}%</span>
                  </div>
                  <div
                    className='devops-progress-track'
                    role='progressbar'
                    aria-valuenow={progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label='Progresso do sprint'
                  >
                    <div className='devops-progress-bar' style={{width: progress + '%'}} />
                  </div>
                </div>
                {error && (
                  <p role='alert' className='p-3 rounded-xl bg-red-50 text-sm text-red-600'>
                    {error}
                  </p>
                )}
                <div className='flex flex-wrap gap-2'>
                  <input
                    aria-label='Buscar tarefas no sprint'
                    value={taskSearch}
                    onChange={(event) => setTaskSearch(event.target.value)}
                    placeholder='Buscar tarefa neste sprint...'
                    className='flex-1 min-w-[160px] px-3 py-2 rounded-xl border border-slate-200 text-sm'
                  />
                  <select
                    aria-label='Filtrar tarefas do sprint'
                    value={taskFilter}
                    onChange={(event) => setTaskFilter(event.target.value)}
                    className='px-3 py-2 rounded-xl border border-slate-200 text-sm'
                  >
                    <option value='all'>Todas as tarefas</option>
                    <option value='pending'>Pendentes</option>
                    <option value='overdue'>Atrasadas</option>
                    <option value='done'>Concluídas</option>
                  </select>
                  {selectedSprint.status !== 'completed' && (
                    <button
                      onClick={() => setShowTaskPicker((current) => !current)}
                      aria-expanded={showTaskPicker}
                      className='devops-primary-button'
                    >
                      <Link2 size={15} />
                      Adicionar tarefas
                    </button>
                  )}
                </div>
              </div>
              {showTaskPicker && selectedSprint.status !== 'completed' && (
                <div className='border-b border-slate-100 bg-slate-50 p-4 md:p-5 space-y-3'>
                  <div className='flex justify-between items-center gap-3'>
                    <div>
                      <h4 className='text-sm font-semibold text-slate-700'>Tarefas disponíveis</h4>
                      <p className='text-xs text-slate-500 mt-1'>
                        Do quadro {selectedBoard?.name}, sem outro sprint.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowTaskPicker(false)}
                      aria-label='Fechar seleção de tarefas'
                      className='devops-icon-button'
                    >
                      <X size={15} />
                    </button>
                  </div>
                  <input
                    value={pickerSearch}
                    onChange={(event) => setPickerSearch(event.target.value)}
                    aria-label='Buscar tarefas disponíveis'
                    placeholder='Buscar tarefas para adicionar...'
                    className='w-full px-3 py-2 rounded-xl border border-slate-200 text-sm'
                  />
                  <div className='space-y-2 max-h-64 overflow-y-auto'>
                    {availableTasks.map((task) => (
                      <div
                        key={task.id}
                        className='flex items-center gap-2 p-3 bg-white rounded-xl border border-slate-200'
                      >
                        <span className='flex-1 text-sm text-slate-700 min-w-0'>{task.title}</span>
                        <Badge className={getStatusConfig(task.status).color}>
                          {getStatusConfig(task.status).label}
                        </Badge>
                        <button
                          onClick={() => handleAssignment(task.id, selectedSprint.id)}
                          disabled={!!assigning}
                          className='devops-secondary-button disabled:opacity-50'
                        >
                          {assigning === task.id ? 'Adicionando...' : 'Adicionar'}
                        </button>
                      </div>
                    ))}
                    {!availableTasks.length && (
                      <p className='text-sm text-slate-500 py-3 text-center'>
                        {pickerSearch
                          ? 'Nenhuma tarefa corresponde à busca.'
                          : 'Não há tarefas disponíveis neste quadro. As tarefas já vinculadas a outro sprint não aparecem aqui.'}
                      </p>
                    )}
                  </div>
                </div>
              )}
              <div className='overflow-x-auto'>
                <table className='w-full min-w-[600px]'>
                  <thead>
                    <tr className='border-b border-slate-100'>
                      {[
                        'Tarefa',
                        'Status',
                        'Prioridade',
                        'Responsável',
                        'Pontos',
                        'Prazo',
                        'Ações',
                      ].map((heading) => (
                        <th
                          key={heading}
                          className='text-left py-3 px-3 text-xs font-semibold text-slate-500'
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleTasks.map((task) => {
                      const statusCfg = getStatusConfig(task.status),
                        priorityCfg = getPriorityConfig(task.priority),
                        assignee = task.assigneeIds[0] ? getUserById(task.assigneeIds[0]) : null
                      return (
                        <tr key={task.id} className='border-b border-slate-50 hover:bg-slate-50'>
                          <td className='py-3 px-3'>
                            <button
                              onClick={() => dispatch({type: 'SELECT_TASK', payload: task.id})}
                              className='text-sm font-medium text-slate-700 hover:text-brand-600 text-left'
                            >
                              {task.title}
                            </button>
                          </td>
                          <td className='py-3 px-3'>
                            <Badge className={statusCfg.color}>{statusCfg.label}</Badge>
                          </td>
                          <td className='py-3 px-3'>
                            <Badge className={priorityCfg.color} dot={priorityCfg.dot}>
                              {priorityCfg.label}
                            </Badge>
                          </td>
                          <td className='py-3 px-3'>
                            {assignee ? (
                              <div className='flex items-center gap-2'>
                                <Avatar
                                  name={assignee.name}
                                  color={assignee.avatarColor}
                                  size='sm'
                                />
                                <span className='text-xs text-slate-600'>
                                  {assignee.name}
                                  {task.assigneeIds.length > 1
                                    ? ' +' + (task.assigneeIds.length - 1)
                                    : ''}
                                </span>
                              </div>
                            ) : (
                              <span className='text-xs text-slate-400'>Sem responsável</span>
                            )}
                          </td>
                          <td className='py-3 px-3 text-sm text-slate-600'>{task.storyPoints}</td>
                          <td
                            className={
                              'py-3 px-3 text-xs ' +
                              (isTaskOverdue(task) ? 'text-red-600 font-medium' : 'text-slate-500')
                            }
                          >
                            {task.dueDate ? formatDate(task.dueDate) : 'Sem prazo'}
                          </td>
                          <td className='py-3 px-3'>
                            {selectedSprint.status !== 'completed' && (
                              <button
                                onClick={() => handleAssignment(task.id, null)}
                                disabled={!!assigning}
                                aria-label={'Remover ' + task.title + ' do sprint'}
                                title='Remover do sprint'
                                className='devops-icon-button disabled:opacity-50 hover:!text-red-500'
                              >
                                <X size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                    {!visibleTasks.length && (
                      <tr>
                        <td colSpan={7} className='text-center py-10 px-4 text-sm text-slate-500'>
                          {sprintTasks.length
                            ? 'Nenhuma tarefa corresponde aos filtros.'
                            : selectedSprint.status === 'completed'
                            ? 'Este sprint foi concluído sem tarefas.'
                            : 'Adicione as tarefas que compõem esta entrega.'}
                          {sprintTasks.length > 0 && (
                            <button
                              className='block mx-auto mt-2 text-brand-600'
                              onClick={() => {
                                setTaskSearch('')
                                setTaskFilter('all')
                              }}
                            >
                              Limpar filtros de tarefas
                            </button>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className='devops-empty-state h-64'>
              <Timer size={28} />
              <p>Selecione um sprint para acompanhar o ciclo.</p>
            </div>
          )}
        </div>
      </div>
      {formSprint && (
        <SprintForm
          sprint={formSprint === 'new' ? undefined : formSprint}
          onClose={() => setFormSprint(null)}
          onSaved={(id) => {
            clearFilters()
            setSelectedSprintId(id)
          }}
        />
      )}
      <ConfirmDialog
        isOpen={!!deleteSprintId}
        title='Excluir sprint'
        message={
          'Excluir “' +
          (state.sprints.find((sprint) => sprint.id === deleteSprintId)?.name || '') +
          '”? As tarefas serão preservadas e desvinculadas deste sprint.'
        }
        confirmLabel='Excluir sprint'
        variant='danger'
        onConfirm={async () => {
          if (deleteSprintId) await apiDeleteSprint(deleteSprintId)
          setDeleteSprintId(null)
        }}
        onCancel={() => setDeleteSprintId(null)}
      />
      <ConfirmDialog
        isOpen={!!transition}
        title={
          transition?.status === 'completed'
            ? 'Concluir sprint'
            : transitioningSprint?.status === 'completed'
            ? 'Reabrir sprint'
            : 'Iniciar sprint'
        }
        message={
          transition?.status === 'completed'
            ? 'Concluir “' +
              (transitioningSprint?.name || '') +
              '”? ' +
              (transitionPending
                ? transitionPending +
                  ' tarefas ainda estão pendentes e continuarão vinculadas ao sprint, sem alteração de status.'
                : 'Todas as tarefas deste ciclo estão concluídas.')
            : 'O sprint “' +
              (transitioningSprint?.name || '') +
              '” passará a ativo. As tarefas manterão seus status atuais.'
        }
        confirmLabel={transition?.status === 'completed' ? 'Concluir sprint' : 'Ativar sprint'}
        variant='warning'
        onConfirm={async () => {
          if (transition) await apiUpdateSprint(transition)
          setTransition(null)
        }}
        onCancel={() => setTransition(null)}
      />
    </div>
  )
}
